/**
 * Create a ZIP archive in memory and trigger a browser download.
 *
 * Uses `fflate` (pure JS, ~8KB gzipped) — no WASM, no fs, works in
 * every browser and in Workers. The archive is built entirely in memory
 * as a Uint8Array and never touches disk.
 */

import { zipSync } from 'fflate';
import { downloadBytes } from './download.ts';

export interface ZipEntry {
  /** Path inside the archive, e.g. "favicon-32x32.png" or "pages/page-1.pdf" */
  name: string;
  /** Raw bytes */
  data: Uint8Array;
}

/**
 * Create a ZIP archive from entries. Returns raw bytes — no DOM needed.
 * Exported separately so tests can verify zip creation without a browser.
 */
export function createZip(entries: ZipEntry[]): Uint8Array {
  const files: Record<string, Uint8Array> = {};
  for (const entry of entries) {
    files[entry.name] = entry.data;
  }
  return zipSync(files, { level: 6 });
}

/**
 * Bundle entries into a .zip and trigger a browser download.
 *
 * @param entries  Files to include in the archive
 * @param zipName  Filename for the downloaded .zip (e.g. "favicons.zip")
 */
export function downloadZip(entries: ZipEntry[], zipName: string): void {
  const zipped = createZip(entries);
  downloadBytes(zipped, zipName, 'application/zip');
}
